import { CircleHostCard } from '@modernhealth/circles-web-components';
import { BodyTextMedium, HeadingText40, HeadingText50 } from '@modernhealth/vital-components';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { CircleTopicBaseFragment } from '@modernhealth/circles-graphql/v2';
import { captureException } from '@sentry/browser';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ErrorFallback } from 'src/modules/dashboard/components/ErrorFallback';

import { Page } from '../components/PageLayout';
import { UnorderedList } from '../components/UnorderedList';
import { BackButton } from '../components/BackButton';
import { CircleOnDemandFauxVideoPlayer } from '../components/CircleOnDemandFauxVideoPlayer';
import { useGetOnDemandCircleBySlugQuery } from '../documents/getOnDemandCircleBySlug.generated';
import { CircleTopicCard } from '../components/CircleTopicCard';

export const CirclesOnDemandCirclePage = () => {
    const intl = useIntl();
    const history = useHistory();
    const { topicSlug = '' } = useParams<{ topicSlug?: string }>();
    const { circlesShowOnDemandFeedback } = useFlags();
    const { data, loading, error } = useGetOnDemandCircleBySlugQuery({
        variables: { slug: topicSlug },
    });

    useEffect(() => {
        if (error) captureException(error);
    }, [error]);

    if (loading) return null;

    const ondemand = data?.circleOnDemandBySlug;

    if (ondemand?.__typename !== 'CircleOnDemandNode') return <ErrorFallback />;

    return (
        <Page.Container>
            <Page.Header>
                <BackButton />
            </Page.Header>
            <Page.Content
                centered
                alignChildren="start"
                css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 40,
                    maxWidth: 560,
                }}
            >
                <CircleOnDemandFauxVideoPlayer
                    seconds={ondemand.video.durationInSeconds}
                    languageCode={ondemand.languageCode}
                    thumb={ondemand.video.thumbnail}
                    name={ondemand.name}
                    externalLink={ondemand.video.externalVideoUrl}
                    timeout={1000}
                    onClick={
                        circlesShowOnDemandFeedback
                            ? () => {
                                  history.push(`/circles/on-demand/${topicSlug}/feedback`);
                              }
                            : undefined
                    }
                />
                <section>
                    <HeadingText50>{ondemand.name}</HeadingText50>
                    <BodyTextMedium>{ondemand.description}</BodyTextMedium>
                </section>
                <section>
                    <HeadingText40 css={{ marginBottom: 24 }}>Hosted by</HeadingText40>
                    <div
                        css={{
                            display: 'grid',
                            gap: 16,
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))',
                        }}
                    >
                        {ondemand.facilitators.map(facilitator => {
                            return (
                                <CircleHostCard
                                    key={facilitator.id}
                                    name={facilitator.name}
                                    photo={facilitator.photo}
                                    credential=""
                                    country={
                                        facilitator.countryCode
                                            ? intl.formatDisplayName(facilitator.countryCode, {
                                                  type: 'region',
                                              })
                                            : undefined
                                    }
                                />
                            );
                        })}
                    </div>
                </section>
                {ondemand.relatedTopics && ondemand.relatedTopics.length > 0 && (
                    <section>
                        <HeadingText40 css={{ marginBottom: 24 }}>
                            Related live events
                        </HeadingText40>
                        <UnorderedList
                            css={{
                                display: 'flex',
                                gap: 16,
                                '& > *': {
                                    flex: 1,
                                },
                            }}
                        >
                            {ondemand.relatedTopics.map(topic => {
                                return (
                                    <CircleTopicCard
                                        topic={topic as CircleTopicBaseFragment}
                                        id={`on-demand-related-${topic.id}`}
                                        key={topic.id}
                                    />
                                );
                            })}
                        </UnorderedList>
                    </section>
                )}
            </Page.Content>
        </Page.Container>
    );
};
